"use client";

import { scopedThreadKey } from "@amoreg/client-runtime/environment";
import { FILL_PREVIEW_VIEWPORT, type ScopedThreadRef } from "@amoreg/contracts";
import { Ellipsis, MoveDiagonal, MoveDiagonal2, PanelRightIcon, PictureInPicture2, XIcon } from "lucide-react";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { sendBrowserChromeAction, useBrowserFrame, useBrowserPreview } from "~/state/browserPreview";
import {
  useActiveBrowserRecordingTabIds,
  findActiveBrowserRecordingRuntimeTabId,
} from "~/browser/browserRecording";
import { BrowserSurfaceSlot } from "~/browser/BrowserSurfaceSlot";
import { useBrowserSurfaceStore } from "~/browser/browserSurfaceStore";
import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";
import { previewRuntimeTabId } from "~/browser/previewRuntimeTabId";
import { Button } from "~/components/ui/button";
import { toastManager } from "~/components/ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { useThreadPreviewState } from "~/previewStateStore";
import {
  selectThreadPreviewMiniPlayer,
  usePreviewMiniPlayerStore,
} from "~/previewMiniPlayerStore";
import { useCloudBrowserViewSetting } from "~/cloudBrowserViewStore";
import { selectActiveRightPanel, useRightPanelStore } from "~/rightPanelStore";

import { browserEngagement } from "./CloudBrowserKeepAwake.logic";
import { previewBridge } from "./previewBridge";
import { SavedLoginsNotice } from "./SavedLoginsNotice";
import {
  measureMiniPlayerLayout,
  MINI_PLAYER_RESIZE_HANDLES,
  sameMiniPlayerLayout,
  type MiniPlayerLayout,
} from "./miniPlayerChrome";
import {
  clampPreviewMiniPlayerPosition,
  cloudMiniPlayerPageSize,
  NO_PREVIEW_MINI_PLAYER_OBSTACLES,
  type PreviewMiniPlayerFrame,
  resizePreviewMiniPlayer,
  resolvePreviewMiniPlayerFrame,
  resolvePreviewMiniPlayerSourceSize,
} from "./previewMiniPlayerLayout";

interface PointerGesture {
  readonly pointerId: number;
  readonly target: HTMLElement;
  readonly pointerX: number;
  readonly pointerY: number;
  readonly frame: PreviewMiniPlayerFrame;
  readonly direction: BrowserViewportResizeDirection | null;
  readonly originalWidth: number | null;
  readonly originalPosition: { readonly x: number; readonly y: number } | null;
  /** A move only starts past DRAG_THRESHOLD_PX, so a click or double-click never pins the player. */
  moved: boolean;
}

const DRAG_THRESHOLD_PX = 4;

interface Props {
  readonly threadRef: ScopedThreadRef;
  readonly tabId: string;
  readonly composerOverlayElement: HTMLElement | null;
}

export function ThreadPreviewMiniPlayer({ threadRef, tabId, composerOverlayElement }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<PointerGesture | null>(null);
  const [layout, setLayout] = useState<MiniPlayerLayout | null>(null);
  const container = layout?.container ?? null;
  const obstacles = layout?.obstacles ?? NO_PREVIEW_MINI_PLAYER_OBSTACLES;
  const miniPlayer = usePreviewMiniPlayerStore((state) =>
    selectThreadPreviewMiniPlayer(state.byThreadKey, threadRef),
  );
  const previewState = useThreadPreviewState(threadRef);
  const snapshot = previewState.sessions[tabId] ?? null;
  const runtimeTabId = previewRuntimeTabId(threadRef, previewState.serverEpoch, tabId);
  const cloudSurface = previewBridge === null;
  // Only the cloud browser carries saved logins; an empty id never fetches.
  const { savedLogins, refresh: refreshAvailability } = useBrowserPreview(
    cloudSurface ? threadRef.threadId : "",
  );
  const { frameSrc } = useBrowserFrame(
    threadRef.threadId,
    cloudSurface && miniPlayer?.tabId === tabId,
    undefined,
    // The agent's tab: the floating player follows the agent's work (the
    // browser's other tabs are in the panel's tab strip).
    null,
  );
  // Availability is not polled while a browser is live, and the browser links
  // to saved logins only after its first frames: re-check shortly after the
  // page appears so the saved-logins notice can show (found in the live check).
  const hasFrame = frameSrc !== null;
  // A running browser floating in front of the person: the page's keep-awake
  // loop keeps it awake while it floats (CloudBrowserKeepAwake).
  const threadKey = scopedThreadKey(threadRef);
  useEffect(() => {
    if (cloudSurface && hasFrame) return browserEngagement.show(threadKey);
  }, [cloudSurface, hasFrame, threadKey]);
  useEffect(() => {
    if (!cloudSurface || !hasFrame || savedLogins) return;
    const soon = setTimeout(refreshAvailability, 1_500);
    const later = setTimeout(refreshAvailability, 6_000);
    return () => {
      clearTimeout(soon);
      clearTimeout(later);
    };
  }, [cloudSurface, hasFrame, savedLogins, refreshAvailability]);
  // The cloud browser's page has the panel's shape; floating, it takes the
  // player's (a device keeps its own), so the page fills the window. Not while
  // the panel shows this browser too: the panel sizes it then, and takes it
  // back when it opens.
  const [cloudViewport] = useCloudBrowserViewSetting(threadRef, "viewport");
  const panelShowsBrowser = useRightPanelStore(
    (state) => selectActiveRightPanel(state.byThreadKey, threadRef) === "preview",
  );
  const floatedPageSize = cloudSurface ? cloudMiniPlayerPageSize(cloudViewport) : null;
  const floatedWidth = floatedPageSize?.width ?? 0;
  const floatedHeight = floatedPageSize?.height ?? 0;
  useEffect(() => {
    if (!hasFrame || panelShowsBrowser || floatedWidth === 0) return;
    void sendBrowserChromeAction(threadRef.threadId, "resize", {
      width: String(Math.round(floatedWidth)),
      height: String(Math.round(floatedHeight)),
    }).catch(() => {});
  }, [hasFrame, panelShowsBrowser, floatedWidth, floatedHeight, threadRef.threadId]);
  const recordingTabIds = useActiveBrowserRecordingTabIds();
  const recording =
    recordingTabIds.has(runtimeTabId) ||
    findActiveBrowserRecordingRuntimeTabId(threadRef, tabId) !== null;
  const desktopOverlay = previewState.desktopByTabId[tabId] ?? null;
  const fittedSourceContent = useBrowserSurfaceStore(
    (state) => state.byTabId[runtimeTabId]?.fittedSourceContent ?? null,
  );
  const source = resolvePreviewMiniPlayerSourceSize(
    (cloudSurface ? cloudViewport : snapshot?.viewport) ?? FILL_PREVIEW_VIEWPORT,
    fittedSourceContent,
    desktopOverlay?.zoomFactor ?? 1,
  );
  const frame =
    container && miniPlayer?.tabId === tabId
      ? resolvePreviewMiniPlayerFrame({
          width: miniPlayer.width,
          position: miniPlayer.position,
          source,
          container,
          obstacles,
        })
      : null;

  const close = () => {
    usePreviewMiniPlayerStore.getState().close(threadRef);
  };

  const openInPanel = () => {
    usePreviewMiniPlayerStore.getState().close(threadRef);
    // The cloud browser is one panel surface whatever the tab (its own strip).
    useRightPanelStore.getState().openBrowser(threadRef, tabId, { single: cloudSurface });
  };

  const toggleNativePictureInPicture = () => {
    if (!previewBridge) return;
    const operation = desktopOverlay?.pictureInPicture
      ? previewBridge.pictureInPicture.close
      : previewBridge.pictureInPicture.open;
    void operation(runtimeTabId).catch((error) => {
      toastManager.add({
        type: "error",
        title: "Unable to update popped-out preview",
        description: error instanceof Error ? error.message : "An error occurred.",
      });
    });
  };

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => {
      const next = measureMiniPlayerLayout(element, composerOverlayElement);
      setLayout((current) => (current && sameMiniPlayerLayout(current, next) ? current : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (composerOverlayElement) observer.observe(composerOverlayElement);
    return () => observer.disconnect();
  }, [composerOverlayElement]);

  const cancelGesture = useCallback(() => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    gestureRef.current = null;
    const store = usePreviewMiniPlayerStore.getState();
    store.resize(threadRef, tabId, gesture.originalWidth);
    store.move(threadRef, tabId, gesture.originalPosition);
    if (gesture.target.hasPointerCapture(gesture.pointerId)) {
      gesture.target.releasePointerCapture(gesture.pointerId);
    }
  }, [threadRef.environmentId, threadRef.threadId, tabId]);

  useLayoutEffect(() => {
    window.addEventListener("blur", cancelGesture);
    return () => {
      window.removeEventListener("blur", cancelGesture);
      cancelGesture();
    };
  }, [cancelGesture]);

  const beginGesture = (
    event: ReactPointerEvent<HTMLElement>,
    direction: BrowserViewportResizeDirection | null,
  ) => {
    if (event.button !== 0 || !frame || !miniPlayer) return;
    gestureRef.current = {
      pointerId: event.pointerId,
      target: event.currentTarget,
      pointerX: event.clientX,
      pointerY: event.clientY,
      frame,
      direction,
      originalWidth: miniPlayer.width,
      originalPosition: miniPlayer.position,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId || !container) return;
    const delta = { x: event.clientX - gesture.pointerX, y: event.clientY - gesture.pointerY };
    const store = usePreviewMiniPlayerStore.getState();
    if (gesture.direction === null) {
      if (!gesture.moved && Math.hypot(delta.x, delta.y) <= DRAG_THRESHOLD_PX) return;
      gesture.moved = true;
      store.resize(threadRef, tabId, gesture.frame.width);
      store.move(
        threadRef,
        tabId,
        clampPreviewMiniPlayerPosition(
          { x: gesture.frame.x + delta.x, y: gesture.frame.y + delta.y },
          container,
          gesture.frame,
          obstacles,
        ),
      );
      return;
    }
    const next = resizePreviewMiniPlayer({
      start: gesture.frame,
      direction: gesture.direction,
      delta,
      source,
      container,
      obstacles,
    });
    store.resize(threadRef, tabId, next.width);
    store.move(threadRef, tabId, { x: next.x, y: next.y });
  };

  const endGesture = (event: ReactPointerEvent<HTMLElement>) => {
    if (gestureRef.current?.pointerId !== event.pointerId) return;
    if (event.type === "pointerup") handlePointerMove(event);
    else {
      cancelGesture();
      return;
    }
    gestureRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  if (!snapshot || miniPlayer?.tabId !== tabId) return null;

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      {frame ? (
        <section
          aria-label="Floating browser preview"
          data-preview-mini-player={tabId}
          className="group/mini pointer-events-none absolute select-none"
          style={{
            left: frame.x,
            top: frame.y,
            width: frame.width,
            height: frame.height,
            borderRadius: 12,
          }}
        >
          <div className="group pointer-events-auto absolute right-2 top-2 z-[31] size-7">
            <div
              role={recording ? "status" : "button"}
              aria-label={recording ? "Recording preview" : "Browser controls"}
              data-preview-mini-player-controls="true"
              className={cn(
                "absolute right-0 top-0 flex size-7 items-center justify-center rounded-full border border-border/80 bg-popover/90 text-foreground shadow-md backdrop-blur-xl transition-opacity group-hover:opacity-0 group-focus-within:opacity-0",
                recording && "ring-2 ring-red-500 motion-safe:animate-status-pulse",
              )}
            >
              <Ellipsis className="size-4" aria-hidden="true" />
            </div>
            <div
              className="pointer-events-none absolute right-0 top-0 flex h-8 cursor-grab items-center gap-0.5 rounded-lg border border-border/80 bg-popover/92 p-0.5 opacity-0 shadow-lg/20 backdrop-blur-xl transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 active:cursor-grabbing"
              data-preview-mini-player-drag="true"
              onPointerDown={(event) => beginGesture(event, null)}
              onPointerMove={handlePointerMove}
              onPointerUp={endGesture}
              onPointerCancel={endGesture}
              onLostPointerCapture={endGesture}
            >
              {recording ? (
                <span
                  aria-hidden="true"
                  className="mx-1 size-2 shrink-0 rounded-full bg-red-500 motion-safe:animate-status-pulse"
                />
              ) : null}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Open preview in right panel"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={openInPanel}
                    />
                  }
                >
                  <PanelRightIcon />
                </TooltipTrigger>
                <TooltipPopup side="top">Open in right panel</TooltipPopup>
              </Tooltip>
              {cloudSurface ? null : (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant={desktopOverlay?.pictureInPicture ? "secondary" : "ghost"}
                        size="icon-xs"
                        aria-label={
                          desktopOverlay?.pictureInPicture
                            ? "Close popped-out preview"
                            : "Pop preview into separate window"
                        }
                        disabled={!desktopOverlay?.hasWebContents}
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={toggleNativePictureInPicture}
                      />
                    }
                  >
                    <PictureInPicture2 />
                  </TooltipTrigger>
                  <TooltipPopup side="top">
                    {desktopOverlay?.pictureInPicture
                      ? "Close separate window"
                      : "Pop into separate window"}
                  </TooltipPopup>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Close floating preview"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={close}
                    />
                  }
                >
                  <XIcon />
                </TooltipTrigger>
                <TooltipPopup side="top">Close floating preview</TooltipPopup>
              </Tooltip>
            </div>
          </div>

          <div className="absolute inset-0 z-[29] rounded-[inherit] bg-muted shadow-2xl/35" />
          {cloudSurface ? (
            frameSrc ? (
              <img
                src={frameSrc}
                alt="Browser preview"
                title="Drag to move · double-click to open in the right panel"
                draggable={false}
                data-preview-mini-player-drag="frame"
                onPointerDown={(event) => beginGesture(event, null)}
                onPointerMove={handlePointerMove}
                onPointerUp={endGesture}
                onPointerCancel={endGesture}
                onLostPointerCapture={endGesture}
                onDoubleClick={openInPanel}
                className="pointer-events-auto absolute inset-0 z-[30] h-full w-full cursor-grab touch-none rounded-[inherit] object-contain active:cursor-grabbing"
              />
            ) : null
          ) : (
            <BrowserSurfaceSlot
              tabId={runtimeTabId}
              visible={Boolean(desktopOverlay?.hasWebContents)}
              cornerRadius={12}
              fitSourceContent
              layoutVersion={`${frame.x}:${frame.y}`}
              className="absolute inset-0"
            />
          )}

          <div className="pointer-events-none absolute inset-0 z-[31] rounded-[inherit] ring-1 ring-inset ring-border/80" />
          {cloudSurface && frameSrc !== null ? (
            <SavedLoginsNotice carriesSavedLogins={savedLogins} variant="floating" />
          ) : null}
          {!(cloudSurface ? frameSrc !== null : Boolean(desktopOverlay?.hasWebContents)) ? (
            <div className="pointer-events-none absolute inset-0 z-[31] flex items-center justify-center rounded-[inherit] bg-muted text-xs text-muted-foreground">
              Reconnecting preview…
            </div>
          ) : null}
          {MINI_PLAYER_RESIZE_HANDLES.map(({ direction, className }) => (
            <div
              key={direction}
              role="presentation"
              data-preview-mini-player-resize={direction}
              className={cn("pointer-events-auto absolute z-[31] touch-none", className)}
              onPointerDown={(event) => beginGesture(event, direction)}
              onPointerMove={handlePointerMove}
              onPointerUp={endGesture}
              onPointerCancel={endGesture}
              onLostPointerCapture={endGesture}
            >
              {direction === "southeast" || direction === "southwest" ? (
                <span
                  aria-hidden="true"
                  className="flex size-5 items-center justify-center rounded-md border border-border/80 bg-popover/90 text-muted-foreground opacity-0 shadow-sm transition-opacity group-hover/mini:opacity-100"
                >
                  {direction === "southeast" ? (
                    <MoveDiagonal2 className="size-3" />
                  ) : (
                    <MoveDiagonal className="size-3" />
                  )}
                </span>
              ) : null}
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
